<template>
  <div class="elastic-force-viz">
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong>F = k · Δx$</div>
    </div>

    <!-- 主展示区 -->
    <div class="main-layout vertical">
      <div class="simulation-container" ref="stage" @mousedown="startDrag" @mousemove="onDrag" @mouseup="stopDrag" @mouseleave="stopDrag">
        <div class="hud-info">
          <div class="stat-badge">
            <label>形变量 (Δx)</label>
            <span>{{ deformation.toFixed(1) }} cm</span>
          </div>
          <div class="stat-badge stress">
            <label>弹力 (F)</label>
            <span>{{ force.toFixed(1) }} N</span>
          </div>
        </div>

        <svg width="100%" height="180" viewBox="0 0 600 180">
          <!-- 背景基准线 -->
          <line x1="100" y1="90" x2="500" y2="90" stroke="#e2e8f0" stroke-width="1" stroke-dasharray="8,4" />
          
          <!-- 固定支撑墙 -->
          <rect x="70" y="40" width="30" height="100" fill="#475569" rx="2" />
          <line x1="100" y1="40" x2="100" y2="140" stroke="#1e293b" stroke-width="4" stroke-linecap="round" />

          <!-- 弹簧核心部分 -->
          <path :d="springPath" fill="none" stroke="#2563eb" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" class="spring-line" />

          <!-- 弹力矢量 -->
          <g v-if="Math.abs(deformation) > 2" :transform="`translate(${handleX}, 90)`">
            <line x1="0" y1="0" :x2="-force * 6" y2="0" stroke="#ef4444" stroke-width="4" marker-end="url(#arrow-red)" />
            <text :x="-force * 6 / 2" y="-12" text-anchor="middle" fill="#ef4444" font-size="12" font-weight="700">F弹</text>
          </g>

          <!-- 交互手柄 -->
          <g class="drag-handle" :transform="`translate(${handleX}, 90)`" style="cursor: grab">
            <circle r="15" fill="rgba(37, 99, 235, 0.1)" class="pulse-ring" />
            <circle r="7" fill="#2563eb" stroke="#ffffff" stroke-width="2" />
          </g>

          <!-- 箭头库 -->
          <defs>
            <marker id="arrow-red" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
              <polygon points="0 0, 10 3.5, 0 7" fill="#ef4444" />
            </marker>
          </defs>
        </svg>
        <div class="drag-hint">按住蓝色圆点水平拖动</div>
      </div>

      <!-- 微观分子视图 -->
      <div class="micro-card">
        <h3>微观合力显示 (分子间距)</h3>
        <div class="molecules-track">
          <div v-for="i in 8" :key="i" class="molecule" :style="moleculeStyle(i)"></div>
          <div class="molecule-line" :style="{ width: (deformation * 0.5 + 40) * 7 + 'px' }"></div>
        </div>
        <div class="state-indicator">
          <span :class="deformation > 0 ? 'tension' : (deformation < 0 ? 'compression' : 'balance')">
            {{ deformation > 0 ? '分子引力 > 斥力 (拉伸)' : (deformation < 0 ? '分子斥力 > 引力 (压缩)' : '合力为零 (平衡态)') }}
          </span>
        </div>
      </div>

      <!-- 物理原理说明 -->
      <div class="discovery-card">
        <i class="el-icon-info"></i>
        <p><strong>物理详解：</strong>当弹簧发生形变时，分子间的平衡距离被打破。拉伸时，分子间引力占据主导；压缩时，斥力急剧增加。宏观表现为与形变量成正比的<strong>回复力</strong>。</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ElasticForceViz',
  props: { knowledgePoint: String },
  data() {
    return { baseX: 100, handleX: 300, isDragging: false, k: 0.6, restLength: 200 }
  },
  computed: {
    deformation() { return (this.handleX - this.baseX - this.restLength) / 2; },
    force() { return this.k * this.deformation; },
    springPath() {
      const segments = 22;
      const startX = this.baseX;
      const endX = this.handleX;
      const width = endX - startX;
      const step = width / segments;
      const amplitude = 18;
      let d = `M ${startX} 90`;
      for (let i = 1; i <= segments; i++) {
        const x = startX + i * step;
        const y = 90 + (i % 2 === 0 ? -amplitude : amplitude) * (i === segments ? 0 : 1);
        if (i === segments) { d += ` L ${x} 90`; } else {
           const prevX = startX + (i - 1) * step;
           const cp1x = prevX + step * 0.25;
           const cp2x = x - step * 0.25;
           d += ` C ${cp1x} ${y}, ${cp2x} ${y}, ${x} ${y}`;
        }
      }
      return d;
    }
  },
  methods: {
    startDrag(e) {
      const rect = e.currentTarget.getBoundingClientRect();
      const containerWidth = rect.width || 1;
      const x = (e.clientX - rect.left) * (600 / containerWidth);
      if (Math.abs(x - this.handleX) < 40) { this.isDragging = true; }
    },
    onDrag(e) {
      if (!this.isDragging) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const containerWidth = rect.width || 1;
      const rawX = (e.clientX - rect.left) * (600 / containerWidth);
      this.handleX = Math.min(Math.max(rawX, 150), 550);
    },
    stopDrag() { this.isDragging = false; },
    moleculeStyle(i) {
      const baseGap = 40;
      const dynamicGap = baseGap + (this.deformation * 0.5);
      const color = this.deformation > 0 ? '#3b82f6' : (this.deformation < 0 ? '#ef4444' : '#94a3b8');
      return {
        left: `${i * dynamicGap}px`,
        background: color,
        boxShadow: `0 0 12px ${color}44`
      }
    }
  }
}
</script>

<style scoped lang="scss">
.elastic-force-viz { flex: 1; display: flex; flex-direction: column; gap: 16px; height: 100%; overflow: hidden; }
.simulation-container { background: #ffffff; border-radius: 14px; position: relative; padding: 20px; border: 1px solid #f1f5f9; cursor: crosshair; }
.hud-info { position: absolute; top: 15px; left: 120px; display: flex; gap: 15px; }
.stat-badge { 
  background: #f8fafc; padding: 6px 14px; border-radius: 8px; border: 1px solid #e2e8f0; border-left: 4px solid #3b82f6;
  label { display: block; font-size: 11px; color: #64748b; font-weight: 600; margin-bottom: 2px; }
  span { font-size: 14px; color: #1e293b; font-weight: 700; font-family: monospace; }
  &.stress { border-left-color: #ef4444; span { color: #ef4444; } }
}
.pulse-ring { animation: pulse 2s infinite cubic-bezier(0.4, 0, 0.6, 1); }
@keyframes pulse { 0% { r: 18; opacity: 0.8; stroke-width: 0; } 100% { r: 28; opacity: 0; stroke-width: 4; } }
.micro-card { 
  background: #f8fafc; padding: 20px; border-radius: 14px; border: 1px solid #f1f5f9;
  h3 { font-size: 14px; color: #1e293b; margin-bottom: 15px; font-weight: 700; border-left: 3px solid #3b82f6; padding-left: 10px; }
}
.molecules-track { height: 40px; position: relative; display: flex; align-items: center; justify-content: center; }
.molecule { position: absolute; width: 14px; height: 14px; border-radius: 50%; transition: all 0.15s ease-out; border: 2px solid #ffffff; z-index: 2; }
.molecule-line { position: absolute; height: 2px; background: #e2e8f0; z-index: 1; }
.state-indicator { 
  margin-top: 15px; text-align: center; font-size: 13px; font-weight: 600;
  .tension { color: #2563eb; } .compression { color: #dc2626; } .balance { color: #64748b; }
}
.drag-hint { text-align: center; font-size: 12px; color: #94a3b8; font-style: italic; margin-top: 10px; }
.discovery-card { background: #f0f7ff; padding: 16px 20px; border-radius: 12px; border: 1px solid #dbeafe; display: flex; gap: 12px; align-items: flex-start; i { color: #3b82f6; font-size: 1.2rem; margin-top: 2px; } p { margin: 0; font-size: 14px; color: #1e40af; line-height: 1.6; strong { color: #1e3a8a; } } }
</style>
